/**
 * ClientNotesDigest — Sawyer 11 client-notes-agreements (F1).
 *
 * Sawyer: "no one's looking at notes" (memo2 [28:10]). The compact read of a
 * client's pinned notes and the notes new to me, used where the desk and the
 * floor already look: the Front Desk row expansion, the client page's
 * "Pinned notes" band on non-Overview tabs, and the dog profile.
 *
 * Pure presentational: no fetch, no "seen" side effect (the caller owns
 * both). Each body is clamped to 3 lines with a 44px "Show more" control
 * when it is long (P7, MAC11).
 */
import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import type { CustomerNote } from '../../lib/api.js';
import {
  NOTE_CLAMP_CHARS,
  formatNoteDate,
  noteAuthor,
  pinAttribution,
} from './client-note-format.js';
import './ClientNotes.css';

export interface ClientNotesDigestProps {
  readonly pinned: ReadonlyArray<CustomerNote>;
  readonly newNotes: ReadonlyArray<CustomerNote>;
  readonly pinnedTotal: number;
  readonly unseenCount: number;
  readonly customerId: string;
  /** 'pinned-only' for the client-page band; 'pinned-and-new' elsewhere. */
  readonly mode: 'pinned-only' | 'pinned-and-new';
  /** Band only: show the first pinned note plus "+N more pinned". */
  readonly compact?: boolean;
  /** Band only: what "+N more pinned" does (e.g. switch to Overview). */
  readonly onShowAllPinned?: () => void;
  /** Hide the footer link (the band sits ON the client page). */
  readonly hideOpenClient?: boolean;
  readonly testId?: string;
}

function DigestNote({
  note,
  showNewTag,
}: {
  note: CustomerNote;
  showNewTag: boolean;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const long = note.body.length > NOTE_CLAMP_CHARS;
  const pin = pinAttribution(note);
  return (
    <li className="ferro-clientnotes__item" data-testid={`client-note-digest-item-${note.id}`}>
      <p
        className={`ferro-clientnotes__body${long && !expanded ? ' ferro-clientnotes__body--clamped' : ''}`}
      >
        {showNewTag && note.is_new ? (
          <span className="ferro-clientnotes__new" data-testid={`client-note-new-${note.id}`}>
            New
          </span>
        ) : null}
        {note.body}
      </p>
      {long ? (
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-clientnotes__more"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
      <p className="ferro-clientnotes__meta">
        {noteAuthor(note)} · {formatNoteDate(note.created_at)}
        {pin ? ` · ${pin}` : ''}
      </p>
    </li>
  );
}

export function ClientNotesDigest({
  pinned,
  newNotes,
  pinnedTotal,
  unseenCount,
  customerId,
  mode,
  compact = false,
  onShowAllPinned,
  hideOpenClient = false,
  testId,
}: ClientNotesDigestProps): JSX.Element | null {
  const shownPinned = compact ? pinned.slice(0, 1) : pinned;
  const morePinned = Math.max(0, pinnedTotal - shownPinned.length);
  const pinnedIds = new Set(pinned.map((n) => n.id));
  // A pinned note that is also new shows once, in the pinned group, tagged.
  const fresh = mode === 'pinned-and-new' ? newNotes.filter((n) => !pinnedIds.has(n.id)) : [];
  if (shownPinned.length === 0 && fresh.length === 0) return null;
  const hiddenNew = mode === 'pinned-and-new' ? Math.max(0, unseenCount - newNotes.length) : 0;

  return (
    <div className="ferro-clientnotes" data-testid={testId}>
      {shownPinned.length > 0 ? (
        <div className="ferro-clientnotes__group">
          <h3 className="ferro-clientnotes__group-title">
            {mode === 'pinned-only' ? 'Pinned notes' : 'Pinned'}
          </h3>
          <ul className="ferro-clientnotes__list">
            {shownPinned.map((n) => (
              <DigestNote key={n.id} note={n} showNewTag={mode === 'pinned-and-new'} />
            ))}
          </ul>
          {morePinned > 0 ? (
            onShowAllPinned ? (
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost ferro-clientnotes__more"
                onClick={onShowAllPinned}
                data-testid="client-notes-more-pinned"
              >
                +{morePinned} more pinned
              </button>
            ) : (
              <p className="ferro-clientnotes__meta" data-testid="client-notes-more-pinned">
                +{morePinned} more pinned
              </p>
            )
          ) : null}
        </div>
      ) : null}

      {fresh.length > 0 ? (
        <div className="ferro-clientnotes__group">
          <h3 className="ferro-clientnotes__group-title">New since you last looked</h3>
          <ul className="ferro-clientnotes__list">
            {fresh.map((n) => (
              <DigestNote key={n.id} note={n} showNewTag />
            ))}
          </ul>
        </div>
      ) : null}

      {hideOpenClient ? null : (
        <Link
          className="ferro-btn ferro-btn--ghost ferro-clientnotes__open"
          to={`/customers/${customerId}`}
          data-testid={`client-notes-open-${customerId}`}
        >
          {hiddenNew > 0 ? `See all ${unseenCount} new notes` : 'Open client'}
        </Link>
      )}
    </div>
  );
}
